import { Autocomplete, Button, CircularProgress, createTheme, Dialog, DialogActions, DialogContent, Stack, ThemeProvider, Box, Typography, TextField } from "@mui/material";
import React, { useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { type ErrorInfo } from "remult";
import * as yup from "yup";
import {
  formatOpportunitySchedule,
  formatOpportunityTimeDescription,
  getOpportunityScheduleDate,
} from "@shared/helpers/format-opportunity-schedule";

import { associationRepo, opportunityRepo, userRepo } from "@repositories";
import { OrganizationRoles, Roles } from "@shared/Roles";
import { Association, type Opportunity, User } from "@entities";
import { FormGoogleAutocomplete } from "../../form-components/form-google-autocomplete/FormGoogleAutocomplete";
import FormProvider, { useForm, useFormConfig } from "../../../helpers/forms/FormProvider";
import { SelectButtonsContainer } from "../../select-buttons/SelectButtonsContainer";
import { FormTextField } from "../../form-components/form-text-field/FormTextField";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useAidRelatedUserSearch } from "./useAidRelatedUserSearch";
import { SelectButton } from "../../select-buttons/SelectButton";
import { EventDayDate } from "../../event-day-date/EventDayDate";
import { provide } from "../../../helpers/wrap-and-provide";
import { useAlert } from "../../../contexts/AlertProvider";
import {
  CardInfoField,
  CardInfoGrid,
  CardInfoHeader,
  CardInfoSection,
  CardInfoView,
} from "../card-info-view/CardInfoView";

import "./AidRelatedCard.scss";
import { CommunicationRecordManager } from "../../CommunicationRecordManager/CommunicationRecordManager";
import MissionList from "../../../pages/missions/components/MissionList";
import PhoneNumberCell from "../../../pages/tables/PhoneNumberCell";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`aid-tabpanel-${index}`}
      aria-labelledby={`aid-tab-${index}`}
      {...other}
      style={{ minHeight: '70vh', maxHeight: '70vh', overflowY: 'auto', padding: '1rem 2rem' }}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

interface AidRelatedCardProps {
  isAdd?: boolean;
  defaultValues?: Association;
  onClose?: () => void;
  requestedOpportunities?: Opportunity[];
  onSave: any;
}
const DEFAULT_VALUES = {
  phoneNumber: "",
  name: "",
  address: "",
  coordinates: { lat: 0, lng: 0 },
  roles: [Roles.User],
};

const validationSchema = yup.object().shape({
  phoneNumber: yup
    .string()
    .required("שדה חובה")
    .matches(/^\+972\d{9}$/, "מספר טלפון חייב להיות מספר ישראלי תקין"),
  name: yup.string().required("שדה חובה"),
});

const AidRelatedCard: FC<AidRelatedCardProps> = ({
  isAdd = false,
  defaultValues,
  onClose,
  requestedOpportunities,
  onSave
}) => {
  const { currOrganization } = useOrganization();
  const { values, setValues, setFieldValue, resetForm } =
    useForm<Partial<User>>();
  const { setAlertText, setSeverity, setTime } = useAlert();
  const [tabValue, setTabValue] = useState(0);
  const [detailsViewMode, setDetailsViewMode] = useState<"info" | "edit">(
    isAdd ? "edit" : "info",
  );
  const navigate = useNavigate();
  const [defaultUserValues, setDefaultUserValues] = useState<Partial<User> | undefined>({});
  const [associationComments, setAssociationComments] = useState("");
  const [allRequestedOpportunities, setAllRequestedOpportunities] = useState<
    Opportunity[]
  >(requestedOpportunities ?? []);

  const {
    usersByPhone: optionalUsers,
    associationsByName: optionalAssociationsByName,
    isPhoneLoading: isLoading,
    isNameLoading,
  } = useAidRelatedUserSearch({
    organizationId: currOrganization?.id,
    phoneNumber: values.phoneNumber,
    name: values.name,
  });

  useEffect(() => {
    if (defaultValues) setDefaultUserValues(defaultValues?.user);
  }, [defaultValues]);

  useEffect(() => {
    setDetailsViewMode(isAdd ? "edit" : "info");
    setAssociationComments(defaultValues?.comments ?? "");
  }, [isAdd, defaultValues?.id]);

  useEffect(() => {
    if (defaultUserValues) setValues(defaultUserValues);
  }, [defaultUserValues]);

  useEffect(() => {
    setAllRequestedOpportunities(requestedOpportunities ?? []);
    const associationId = defaultValues?.id;
    if (!associationId) return;
    let cancelled = false;
    void opportunityRepo
      .find({
        where: {
          associationRequestedId: associationId,
          isDeleted: false,
        },
        orderBy: { createdAt: "desc" },
      })
      .then((rows) => {
        if (!cancelled) setAllRequestedOpportunities(rows);
      });
    return () => {
      cancelled = true;
    };
  }, [defaultValues?.id, requestedOpportunities]);

  useEffect(() => {
    setFieldValue(
      "phoneNumber",
      values.phoneNumber?.startsWith("0")
        ? "+972" + values.phoneNumber.slice(1, values.phoneNumber.length || 10)
        : values.phoneNumber || "",
    );
  }, [values.phoneNumber]);

  useFormConfig(
    (form) => {
      form.onSubmit = async (values) => {
        try {
          let association = defaultValues;
          if (isAdd) {
            const user = await userRepo.findOne({ where: { phoneNumber: values.phoneNumber }, include: { associations: true } });
            if (user) {
              association = user.associations?.find((a) => a.organizationId === currOrganization?.id);
              if (association) {
                if (association.roles.includes(OrganizationRoles.Requesting)) {
                } else {

                  await associationRepo.update(association.id, {
                    roles: [...association.roles, OrganizationRoles.Requesting],
                  });
                }
              } else {

                association = await associationRepo.insert({
                  userId: user.id,
                  organizationId: currOrganization?.id || "",
                  roles: [OrganizationRoles.Requesting],
                });
              }
            } else {
              const user = await userRepo.insert({
                ...values,
                phoneNumber: values.phoneNumber?.startsWith("+972")
                  ? values.phoneNumber
                  : "+972" + values.phoneNumber,
                roles: [Roles.User],
              });
              association = await associationRepo.insert({
                userId: user.id,
                organizationId: currOrganization?.id || "",
                roles: [OrganizationRoles.Requesting],
              });
            }
          } else if (defaultValues?.user?.id) {
            await userRepo.update(defaultValues.user.id, {
              name: values.name,
              phoneNumber: values.phoneNumber,
              address: values.address,
              numberID: values.numberID,
              coordinates: values.coordinates,
            });
          }
          const associationId = association?.id ?? defaultValues?.id;
          if (associationId) {
            await associationRepo.update(associationId, {
              comments: associationComments,
            });
          }
          onSave(association);
          resetForm();
          if (onClose) onClose();
          setAlertText("המשתמש נשמר בהצלחה");
          setSeverity("success");
          // UserRequestController.aidRelatedTableChange({
          //   change: Math.random(),
          // });
        } catch (e) {
          const error = e as ErrorInfo<User>;
          const heb = { ["already exists"]: "כבר קיים במערכת" };
          if (error && error.modelState) {
            const fields = Object.keys(error.modelState!).map((key) => ({
              key,
              caption: userRepo.metadata.fields.find(key).caption,
            }));
            if (onClose) onClose();
            setTime(6000);
            return setAlertText(
              `קרתה שגיאה באחד מהשדות הבאים: \n${fields
                .map(({ key, caption }) => {
                  const errorType = error!.modelState![
                    key as keyof User
                  ]! as keyof typeof heb;
                  return `   • ${caption}: ${errorType in heb ? heb[errorType] : errorType}`;
                })
                .join("\n")}`,
            );
          }
          setAlertText("התרחשה שגיאה בשמירת המשתמש");
          // eslint-disable-next-line no-console -- error handling
          console.error("error: ", error);
        }
      };
    },
    [values, isAdd, defaultValues, associationComments, currOrganization?.id],
  );

  function handleUserSelect(user: Partial<User>) {
    setValues((prev) => ({ ...prev, ...user }));
  }

  const theme = createTheme({
    palette: {
      primary: {
        main: "#05204CF2",
      },
      secondary: {
        main: "#05204CF2",
      },
    },
  });

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const renderDetailsContent = () => (
    <Stack height="100%" spacing={2}>
      <Typography variant="h5" color="secondary" sx={{ mb: 3, fontWeight: 'bold' }}>
        {isAdd || !!defaultUserValues ? (defaultUserValues ? "פרטי" : "הוספת") : ""} מבקש סיוע
      </Typography>
      <Autocomplete
        options={optionalUsers}
        loading={isLoading}
        filterOptions={(x) => x}
        getOptionLabel={(option) => option.name}
        getOptionKey={(option) => option.id}
        loadingText="טוען..."
        clearText="מחיקה"
        openText="פתיחה"
        closeText="סגירה"
        noOptionsText={
          values.phoneNumber && !optionalUsers.length && !isLoading ? (
            <Stack direction="row" alignItems="center" gap={1}>
              {" "}
              מבקש סיוע חדש (ניתן להמשיך לשדה הבא)
            </Stack>
          ) : (
            "חיפוש/הוספת מספר טלפון"
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        clearOnBlur={false}
        onChange={(_, user, reason) => {
          if (reason === "selectOption" && user) {
            handleUserSelect(user);
          } else if (reason === "clear") {
            setFieldValue("phoneNumber", "");
          }
        }}
        inputValue={
          values.phoneNumber?.startsWith("+972")
            ? 0 + values.phoneNumber.slice(4, values.phoneNumber.length)
            : values.phoneNumber || ""
        }
        onInputChange={(_, newValue, reason) => {
          if (reason === "input") {
            setFieldValue("phoneNumber", newValue);
          }
        }}
        renderInput={(params) => (
          <FormTextField
            label="מספר טלפון"
            name="phoneNumber"
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
      <Autocomplete
        options={optionalAssociationsByName}
        loading={isNameLoading}
        filterOptions={(x) => x}
        getOptionLabel={(option) => option.user?.name ?? ""}
        getOptionKey={(option) => option.id}
        loadingText="טוען..."
        clearText="מחיקה"
        openText="פתיחה"
        closeText="סגירה"
        noOptionsText={
          values.name && !optionalAssociationsByName.length && !isNameLoading ? (
            <Stack direction="row" alignItems="center" gap={1}>
              מבקש סיוע חדש (ניתן להמשיך לשדה הבא)
            </Stack>
          ) : (
            "חיפוש לפי שם"
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        clearOnBlur={false}
        onChange={(_, association, reason) => {
          if (reason === "selectOption" && association?.user) {
            handleUserSelect(association.user);
          } else if (reason === "clear") {
            setFieldValue("name", "");
          }
        }}
        inputValue={values.name || ""}
        onInputChange={(_, newValue, reason) => {
          if (reason === "input") {
            setFieldValue("name", newValue);
          }
        }}
        renderInput={(params) => (
          <FormTextField
            label="שם מלא"
            name="name"
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isNameLoading && (
                    <CircularProgress color="inherit" size={20} />
                  )}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
      <FormTextField name="numberID" label="מספר ת.ז" />
      <FormGoogleAutocomplete
        placeValue={defaultUserValues?.address}
        name="address"
        label="כתובת"
        onLocationChange={({ locationName, latLng }) => {
          setFieldValue("address", locationName);
          setFieldValue("coordinates", latLng);
        }}
      />
      <TextField
        label="הערות"
        value={associationComments}
        onChange={(e) => setAssociationComments(e.target.value)}
        multiline
        minRows={3}
        fullWidth
      />
    </Stack>
  );

  const renderInfoContent = () => {
    const source = (defaultUserValues || values || {}) as Partial<User>;
    return (
      <CardInfoView>
        <CardInfoSection>
          <CardInfoHeader
            title={source.name || "-"}
            onEdit={() => setDetailsViewMode("edit")}
            subtitle={
              source.phoneNumber ? (
                <PhoneNumberCell phoneNumber={source.phoneNumber} />
              ) : (
                <Typography variant="body2">אין טלפון</Typography>
              )
            }
          />
        </CardInfoSection>
        <CardInfoSection title="פרטים">
          <CardInfoGrid>
            <CardInfoField label="כתובת" fullWidth>
              {source.address || "-"}
            </CardInfoField>
            <CardInfoField label="מספר ת.ז">{source.numberID || "-"}</CardInfoField>
            <CardInfoField label="הערות" fullWidth>
              {defaultValues?.comments || "-"}
            </CardInfoField>
          </CardInfoGrid>
        </CardInfoSection>
      </CardInfoView>
    );
  };

  const renderRequestsContent = () => (
    allRequestedOpportunities?.length ? (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "0.5rem",
          flexWrap: "wrap",
          padding: "1rem",
        }}
      >
        {allRequestedOpportunities.map((opportunity, index) => (
          <div
            className="event-details"
            onClick={() => {
              navigate(`/events/${opportunity.id}`);
            }}
            style={{
              border: "2px solid gray",
              borderRadius: "1rem",
              padding: "0.5rem",
              width: "46%",
              cursor: "pointer",
            }}
            key={index}
          >
            <EventDayDate
              date={getOpportunityScheduleDate(opportunity)}
              fallbackLabel={
                !opportunity.hasTime
                  ? formatOpportunityTimeDescription(opportunity)
                  : undefined
              }
              participants={opportunity.participantsCount || 0}
              amount={opportunity.amount || 1}
              id={opportunity.id}
              isFull={opportunity.isFull}
              isDate
            />
            <p className="par">{opportunity.title}</p>
            <p className="par">{formatOpportunitySchedule(opportunity)}</p>
            <p className="par">{opportunity.location}</p>
          </div>
        ))}
      </div>
    ) : (
      <p style={{ padding: "1rem" }}>לא נמצאו בקשות משוייכות</p>
    )
  );

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        PaperProps={{
          sx: {
            minWidth: "50vw",
            maxWidth: "50vw",
            height: '80vh',
            maxHeight: '80vh'
          },
        }}
        disablePortal
        open={isAdd || !!defaultValues}
        onClose={() => {
          resetForm();
          setDefaultUserValues(undefined);
          if (onClose) onClose();
        }}
        className="aid-related-card-popup"
      >
        <Box sx={{
          padding: '0.75rem',
          marginBottom: '1rem'
        }}>
          <SelectButtonsContainer>
            <SelectButton
              onClick={() => handleTabChange(0)}
              content="פרטים"
              isChosen={tabValue === 0}
            />
            {!isAdd && <SelectButton
              onClick={() => handleTabChange(1)}
              content="בקשות"
              isChosen={tabValue === 1}
            />}
            {currOrganization?.organizationType === "PREMIUM" && !isAdd && <SelectButton
              onClick={() => handleTabChange(2)}
              content="תקשורת"
              isChosen={tabValue === 2}
            />}
            {currOrganization?.organizationType === "PREMIUM" && !isAdd &&
              <SelectButton
                onClick={() => handleTabChange(3)}
                content="מטלות"
                isChosen={tabValue === 3}
              />}
          </SelectButtonsContainer>
        </Box>

        <DialogContent sx={{ p: 0, height: '100%' }}>
          <TabPanel value={tabValue} index={0} >
            <Box>{!isAdd && detailsViewMode === "info" ? renderInfoContent() : renderDetailsContent()}</Box>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {renderRequestsContent()}
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {defaultValues?.id && (
              <CommunicationRecordManager associationId={defaultValues.id} type="aid-related" />
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <MissionList
              missionType="AID_RELATED"
              filter={defaultValues?.id ? { associationId: defaultValues?.id } : undefined}
              mission={{ associationId: defaultValues?.id || "", association: defaultValues as Association }}
            />
          </TabPanel>
        </DialogContent>

        {tabValue === 0 && (isAdd || detailsViewMode === "edit") && (
          <DialogActions sx={{ padding: 2, gap: 1 }}>
            {!isAdd && (
              <Button
                variant="outlined"
                onClick={() => {
                  if (defaultUserValues) setValues(defaultUserValues);
                  setAssociationComments(defaultValues?.comments ?? "");
                  setDetailsViewMode("info");
                }}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                ביטול
              </Button>
            )}
            <Button
              type="submit"
              color="secondary"
              variant="contained"
              sx={{ padding: "0.5rem 2rem" }}
              className="save-button"
            >
              שמירה
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </ThemeProvider>
  );
};

export default provide([
  FormProvider<Partial<User>>,
  { initialValues: DEFAULT_VALUES, onSubmit: () => { }, validationSchema },
])(AidRelatedCard);
